import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { GrowthForecastRun, GrowthIntelligenceService, parseJsonRows } from './growth-intelligence.service';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc } from './growth-datetime.util';

interface ScenarioRow {
  variableKey: string;
  adjustmentPercent: number;
}

interface ForecastPointRow {
  day?: number;
  dateUtc?: string;
  value?: number;
  unit?: string;
}

@Component({
  standalone: true,
  selector: 'ain-growth-forecast',
  imports: [
    AinPageComponent, AinStateComponent, AinFormFieldComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule,
    AinDatePipe
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.intelligence.forecast.title')" [description]="t('growth.intelligence.forecast.description')">
      <ain-info-hint page-title-addon [titleKey]="'growth.intelligence.forecast.help.title'" [descriptionKey]="'growth.intelligence.forecast.help.description'"
        [bulletKeys]="['growth.intelligence.forecast.help.bullets.scenario', 'growth.intelligence.forecast.help.bullets.advisory']" />

      <section class="card">
        <div class="grid">
          <ain-form-field [label]="t('growth.intelligence.forecast.fields.forecastType')">
            <p-select [options]="forecastTypeOptions" optionLabel="label" optionValue="value" [(ngModel)]="form.forecastType" appendTo="body" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.forecast.fields.horizonDays')"><p-inputNumber [(ngModel)]="form.horizonDays" [min]="1" [max]="365" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.forecast.fields.periodStart')"><ain-date-input [(ngModel)]="form.periodStartUtc" /></ain-form-field>
          <ain-form-field [label]="t('growth.intelligence.forecast.fields.periodEnd')"><ain-date-input [(ngModel)]="form.periodEndUtc" /></ain-form-field>
        </div>

        <div class="section-title"><h4>{{ t('growth.intelligence.forecast.sections.scenario') }}</h4></div>
        @for (row of scenarioRows; track $index) {
          <div class="row-grid">
            <ain-form-field [label]="t('growth.intelligence.forecast.fields.variableKey')"><input pInputText [(ngModel)]="row.variableKey" /></ain-form-field>
            <ain-form-field [label]="t('growth.intelligence.forecast.fields.adjustmentPercent')"><p-inputNumber [(ngModel)]="row.adjustmentPercent" [min]="-100" [max]="200" /></ain-form-field>
            <button pButton type="button" severity="danger" icon="pi pi-trash" (click)="removeScenarioRow($index)"></button>
          </div>
        }
        <button pButton type="button" severity="secondary" [label]="t('common.add')" (click)="addScenarioRow()"></button>
        <button pButton type="button" [label]="t('growth.intelligence.forecast.actions.run')" (click)="runForecast()"></button>
      </section>

      <section class="card">
        <div class="section-title"><h3>{{ t('growth.intelligence.forecast.sections.results') }}</h3></div>
        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (resultRows().length === 0) {
          <ain-state type="empty" [title]="t('growth.intelligence.forecast.emptyResults')" />
        } @else {
          <p-table [value]="resultRows()" [rows]="14" [paginator]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.intelligence.forecast.columns.day') }}</th>
                <th>{{ t('growth.intelligence.forecast.columns.date') }}</th>
                <th>{{ t('growth.intelligence.forecast.columns.value') }}</th>
                <th>{{ t('growth.intelligence.forecast.columns.unit') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.day ?? '-' }}</td>
                <td>{{ row.dateUtc ? (row.dateUtc | ainDate) : '-' }}</td>
                <td>{{ row.value ?? 0 }}</td>
                <td>{{ row.unit ?? '-' }}</td>
              </tr>
            </ng-template>
          </p-table>
          @if (lastRun()) {
            <p class="meta">{{ t('growth.intelligence.forecast.fields.status') }}: {{ lastRun()!.status }}</p>
          }
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .section-title { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .row-grid { display: grid; gap: 8px; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); align-items: end; margin-bottom: 8px; }
    .meta { color: #6b7280; font-size: 0.875rem; margin: 0; }
  `]
})
export class GrowthForecastComponent {
  private readonly service = inject(GrowthIntelligenceService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly lastRun = signal<GrowthForecastRun | null>(null);
  readonly resultRows = signal<ForecastPointRow[]>([]);

  form = {
    forecastType: 'Revenue',
    horizonDays: 30,
    periodStartUtc: defaultCalendarFromUtc(),
    periodEndUtc: defaultCalendarToUtc()
  };
  scenarioRows: ScenarioRow[] = [{ variableKey: 'conversionRate', adjustmentPercent: 0 }];

  readonly forecastTypeOptions = [
    { value: 'Revenue', label: 'Revenue' },
    { value: 'Lead', label: 'Lead' },
    { value: 'Pipeline', label: 'Pipeline' },
    { value: 'Growth', label: 'Growth' }
  ];

  runForecast(): void {
    this.loading.set(true);
    this.service.runForecast({
      forecastType: this.form.forecastType,
      periodStartUtc: combineUtcDateAndTime(this.form.periodStartUtc, '00:00')!,
      periodEndUtc: combineUtcDateAndTime(this.form.periodEndUtc, '23:59')!,
      horizonDays: this.form.horizonDays,
      scenarioJson: JSON.stringify(this.scenarioRows),
      idempotencyKey: `forecast-${this.form.forecastType}-${Date.now()}`
    }).subscribe({
      next: result => {
        this.lastRun.set(result.run);
        this.resultRows.set(parseJsonRows<ForecastPointRow>(result.run.forecastJson));
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  addScenarioRow(): void { this.scenarioRows.push({ variableKey: '', adjustmentPercent: 0 }); }
  removeScenarioRow(index: number): void { this.scenarioRows.splice(index, 1); }
  t(key: string): string { return this.i18n.translate(key); }
}
